import React, { useState, useEffect, useRef } from 'react';
import './index.css';

// ── Logo SVG Component ─────────────────────────────────────────────────
const Logo = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Outer ring */}
    <circle cx="20" cy="20" r="18" stroke="#E8A020" strokeWidth="1.5"/>
    {/* Gear teeth - simplified */}
    {[0,45,90,135,180,225,270,315].map((angle, i) => (
      <rect
        key={i}
        x="18.5" y="1"
        width="3" height="5"
        fill="#E8A020"
        transform={`rotate(${angle} 20 20)`}
      />
    ))}
    {/* Inner circle */}
    <circle cx="20" cy="20" r="9" fill="#E8A020" fillOpacity="0.08" stroke="#E8A020" strokeWidth="1"/>
    {/* Steering wheel spokes */}
    <line x1="20" y1="12" x2="20" y2="17" stroke="#E8A020" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="20" y1="23" x2="20" y2="28" stroke="#E8A020" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="12" y1="20" x2="17" y2="20" stroke="#E8A020" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="23" y1="20" x2="28" y2="20" stroke="#E8A020" strokeWidth="1.5" strokeLinecap="round"/>
    {/* Center hub */}
    <circle cx="20" cy="20" r="3" fill="#E8A020"/>
  </svg>
);

// ── Gear SVG Decoration ────────────────────────────────────────────────
const GearDeco = ({ size = 80, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {[0,30,60,90,120,150,180,210,240,270,300,330].map((angle, i) => (
      <rect key={i} x="37" y="2" width="6" height="10" rx="1" fill="currentColor"
        transform={`rotate(${angle} 40 40)`}/>
    ))}
    <circle cx="40" cy="40" r="28" stroke="currentColor" strokeWidth="2" fill="none"/>
    <circle cx="40" cy="40" r="12" stroke="currentColor" strokeWidth="2" fill="none"/>
    <circle cx="40" cy="40" r="4" fill="currentColor"/>
  </svg>
);

// ── Counter Hook ───────────────────────────────────────────────────────
function useCounter(target, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(ease * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

// ── Intersection Hook ──────────────────────────────────────────────────
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
}

// ── Navbar ─────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);
  const links = ['About', 'Services', 'Careers', 'Contact'];
  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
      background: scrolled ? 'rgba(13, 13, 13, 0.95)' : 'transparent',
      backdropFilter: scrolled ? 'blur(12px)' : 'none',
      borderBottom: scrolled ? '1px solid rgba(255,255,255,0.05)' : 'none',
      transition: 'all 0.4s ease',
      padding: '0 5%',
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 72 }}>
        {/* Logo */}
        <a href="#home" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
          <Logo size={36} />
          <div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1rem', fontWeight: 700, color: '#E8E8E8', lineHeight: 1.1 }}>Automotive</div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.18em', color: '#E8A020', textTransform: 'uppercase' }}>Talent Hub</div>
          </div>
        </a>
        {/* Desktop Links */}
        <div style={{ display: 'flex', gap: 40, alignItems: 'center' }} className="desktop-nav">
          {links.map(l => (
            <a key={l} href={`#${l.toLowerCase()}`} className="nav-link">{l}</a>
          ))}
          <a href="#contact" className="btn-primary" style={{ padding: '10px 24px' }}>Hire Now</a>
        </div>
        {/* Mobile hamburger */}
        <button onClick={() => setMenuOpen(!menuOpen)}
          style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 8, color: '#E8E8E8' }}
          className="mobile-menu-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            {menuOpen
              ? <><line x1="4" y1="4" x2="20" y2="20" stroke="#E8A020" strokeWidth="2"/><line x1="20" y1="4" x2="4" y2="20" stroke="#E8A020" strokeWidth="2"/></>
              : <><line x1="3" y1="7" x2="21" y2="7" stroke="currentColor" strokeWidth="2"/><line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2"/><line x1="3" y1="17" x2="21" y2="17" stroke="currentColor" strokeWidth="2"/></>
            }
          </svg>
        </button>
      </div>
      {/* Mobile menu */}
      {menuOpen && (
        <div style={{ background: '#141414', borderTop: '1px solid rgba(255,255,255,0.06)', padding: '24px 5%' }}>
          {links.map(l => (
            <a key={l} href={`#${l.toLowerCase()}`} onClick={() => setMenuOpen(false)}
              style={{ display: 'block', padding: '14px 0', color: '#B8B8B8', fontFamily: "'DM Mono', monospace", fontSize: '0.8rem', letterSpacing: '0.14em', textTransform: 'uppercase', textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              {l}
            </a>
          ))}
        </div>
      )}
    </nav>
  );
}

// ── Hero Section ───────────────────────────────────────────────────────
function Hero() {
  return (
    <section id="home" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden', paddingTop: 72 }} className="grid-bg">
      {/* Ambient glow */}
      <div style={{ position: 'absolute', top: '30%', left: '50%', transform: 'translateX(-50%)', width: 600, height: 400, background: 'radial-gradient(ellipse, rgba(232,160,32,0.08) 0%, transparent 70%)', pointerEvents: 'none' }}/>
      {/* Decorative gears */}
      <GearDeco size={160} className="gear-spin float-anim"
        style={{ position: 'absolute', right: '-40px', top: '15%', color: 'rgba(232,160,32,0.06)', display: 'block' }} />
      <GearDeco size={90} className="gear-spin-rev"
        style={{ position: 'absolute', left: '3%', bottom: '20%', color: 'rgba(232,160,32,0.05)', display: 'block' }} />

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 5%', width: '100%', position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: 760 }}>
          <div className="section-label animate-fade-up delay-1" style={{ marginBottom: 24 }}>
            ◈ &nbsp;India's Automotive Career Platform
          </div>
          <h1 className="animate-fade-up delay-2"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2.8rem, 6vw, 5.2rem)', fontWeight: 700, lineHeight: 1.08, marginBottom: 28, color: '#E8E8E8' }}>
            Where Automotive<br/>
            <span className="shimmer-text">Talent Meets</span><br/>
            Opportunity
          </h1>
          <p className="animate-fade-up delay-3"
            style={{ fontSize: 'clamp(1rem, 1.8vw, 1.15rem)', color: '#888', lineHeight: 1.8, marginBottom: 48, maxWidth: 520, fontWeight: 300 }}>
            Connecting skilled professionals with India's leading Automotive manufacturers, dealerships, and auto-tech companies. Purpose-built for the mobility sector.
          </p>
          <div className="animate-fade-up delay-4" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <a href="#services" className="btn-primary pulse-amber">Explore Opportunities</a>
            <a href="#about" className="btn-outline">Learn More</a>
          </div>

          {/* Stats strip */}
          <div className="animate-fade-up delay-5" style={{ display: 'flex', gap: 48, marginTop: 72, paddingTop: 40, borderTop: '1px solid rgba(255,255,255,0.06)', flexWrap: 'wrap' }}>
            {[['500+', 'Jobs Listed'], ['200+', 'Companies'], ['10K+', 'Candidates'], ['Pune', 'Headquarters']].map(([num, label]) => (
              <div key={label}>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.8rem', fontWeight: 700, color: '#E8A020', lineHeight: 1 }}>{num}</div>
                <div style={{ fontSize: '0.72rem', color: '#666', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: 6, fontFamily: "'DM Mono', monospace" }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scrolldown cue */}
      <div style={{ position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }} className="animate-fade-in delay-8">
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.2em', color: '#444', textTransform: 'uppercase' }}>Scroll</div>
        <div style={{ width: 1, height: 40, background: 'linear-gradient(to bottom, #E8A020, transparent)' }}/>
      </div>
    </section>
  );
}

// ── Ticker / Marquee ───────────────────────────────────────────────────
// function Ticker() {
//   const items = ['Maruti Suzuki', 'Tata Motors', 'Mahindra', 'Hero MotoCorp', 'Bajaj Auto', 'TVS Motor', 'Ashok Leyland', 'Eicher Motors', 'Force Motors', 'Piaggio India'];
//   return (
//     <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)', padding: '18px 0', overflow: 'hidden', background: '#0d0d0d', position: 'relative' }}>
//       <div style={{ display: 'flex', width: 'max-content' }} className="marquee">
//         {[...items, ...items].map((item, i) => (
//           <span key={i} style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.7rem', letterSpacing: '0.16em', color: '#444', textTransform: 'uppercase', padding: '0 40px', borderRight: i % items.length !== items.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none', whiteSpace: 'nowrap' }}>
//             {item}
//           </span>
//         ))}
//       </div>
//     </div>
//   );
// }

// ── About Section ──────────────────────────────────────────────────────
function About() {
  const [ref, inView] = useInView();
  return (
    <section id="about" ref={ref} style={{ padding: 'clamp(80px, 10vw, 120px) 5%', background: '#111111' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 80, alignItems: 'center' }}>
          {/* Left */}
          <div>
            <div className={`section-label ${inView ? 'animate-fade-up' : ''}`} style={{ marginBottom: 20 }}>◈ &nbsp;Who We Are</div>
            <div className={`divider-amber ${inView ? 'animate-fade-up delay-1' : ''}`} style={{ marginBottom: 32 }}/>
            <h2 className={`${inView ? 'animate-fade-up delay-2' : ''}`}
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, lineHeight: 1.15, color: '#E8E8E8', marginBottom: 24 }}>
              Built for India's<br/>Auto Industry
            </h2>
            <p className={`${inView ? 'animate-fade-up delay-3' : ''}`}
              style={{ color: '#777', lineHeight: 1.9, fontSize: '0.95rem', marginBottom: 20, fontWeight: 300 }}>
              AutomotiveTalentHub was founded with a single focus: the Automotive industry. We understand that hiring a skilled service engineer or finding an OEM procurement head requires domain knowledge — not generic job board algorithms.
            </p>
            <p className={`${inView ? 'animate-fade-up delay-4' : ''}`}
              style={{ color: '#777', lineHeight: 1.9, fontSize: '0.95rem', marginBottom: 36, fontWeight: 300 }}>
              Based in Pune — India's Automotive capital — we are close to where the industry operates. Our network spans OEMs, Tier-1 suppliers, dealerships, and emerging EV startups across India.
            </p>
            <a href="#services" className={`btn-outline ${inView ? 'animate-fade-up delay-5' : ''}`}>What We Do →</a>
          </div>
          {/* Right — pillars */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {[
              { icon: '⚙', title: 'Domain First', desc: 'Every role on our platform is automotive-specific. No generic listings.' },
              { icon: '🤝', title: 'Direct Connect', desc: 'Candidates talk directly with hiring managers. No middlemen.' },
              { icon: '📍', title: 'India Focused', desc: 'Built for Indian market dynamics — Tier 1 to Tier 3 cities.' },
              { icon: '🔒', title: 'Verified Network', desc: 'Every employer is manually verified before posting jobs.' },
            ].map(({ icon, title, desc }, i) => (
              <div key={title} className={`card-hover ${inView ? 'animate-fade-up' : ''}`}
                style={{ animationDelay: `${(i + 3) * 0.12}s`, background: '#141414', border: '1px solid rgba(255,255,255,0.06)', padding: 24 }}>
                <div style={{ fontSize: '1.4rem', marginBottom: 12 }}>{icon}</div>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.88rem', color: '#E8E8E8', marginBottom: 8 }}>{title}</div>
                <div style={{ fontSize: '0.8rem', color: '#666', lineHeight: 1.7 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Services Section ───────────────────────────────────────────────────
function Services() {
  const [ref, inView] = useInView();
  const services = [
    {
      num: '01',
      title: 'Job Listings',
      desc: 'Curated openings across engineering, sales, after-sales, R&D, supply chain, and EV roles in the Automotive sector.',
      tags: ['Full-Time', 'Contract', 'Fresher', 'Senior'],
      icon: (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <rect x="3" y="7" width="22" height="17" rx="1" stroke="#E8A020" strokeWidth="1.5"/>
          <path d="M9 7V5a1 1 0 011-1h8a1 1 0 011 1v2" stroke="#E8A020" strokeWidth="1.5"/>
          <line x1="8" y1="14" x2="20" y2="14" stroke="#E8A020" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="8" y1="18" x2="16" y2="18" stroke="#E8A020" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      )
    },
    {
      num: '02',
      title: 'Employer Connect',
      desc: 'We partner with OEMs, Tier-1 suppliers, and dealership networks to source pre-screened, industry-ready talent.',
      tags: ['Bulk Hiring', 'Executive Search', 'Campus Drive'],
      icon: (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <circle cx="10" cy="10" r="4" stroke="#E8A020" strokeWidth="1.5"/>
          <circle cx="21" cy="10" r="4" stroke="#E8A020" strokeWidth="1.5"/>
          <path d="M3 23c0-4 3-7 7-7" stroke="#E8A020" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M25 23c0-4-3-7-7-7" stroke="#E8A020" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="14" y1="10" x2="17" y2="10" stroke="#E8A020" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      )
    },
    {
      num: '03',
      title: 'Career Growth',
      desc: 'Resume crafting for automotive profiles, interview coaching, and career counselling aligned with industry pay bands.',
      tags: ['Resume Help', 'Mock Interviews', 'Salary Intel'],
      icon: (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <polyline points="4,22 10,14 16,18 24,8" stroke="#E8A020" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <polyline points="20,8 24,8 24,12" stroke="#E8A020" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
  ];
  return (
    <section id="services" ref={ref} style={{ padding: 'clamp(80px, 10vw, 120px) 5%', background: '#0D0D0D', position: 'relative' }} className="grid-bg">
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 0%, rgba(232,160,32,0.05) 0%, transparent 60%)', pointerEvents: 'none' }}/>
      <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: 72 }}>
          <div className={`section-label ${inView ? 'animate-fade-up' : ''}`} style={{ marginBottom: 20 }}>◈ &nbsp;What We Offer</div>
          <h2 className={`${inView ? 'animate-fade-up delay-2' : ''}`}
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, color: '#E8E8E8', marginBottom: 16 }}>
            End-to-End Hiring Solutions
          </h2>
          <p className={`${inView ? 'animate-fade-up delay-3' : ''}`}
            style={{ color: '#666', maxWidth: 480, margin: '0 auto', fontSize: '0.92rem', lineHeight: 1.8 }}>
            From entry-level to CXO — every role in the Automotive value chain, handled with precision.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
          {services.map(({ num, title, desc, tags, icon }, i) => (
            <div key={title} className={`card-hover ${inView ? 'animate-fade-up' : ''}`}
              style={{ animationDelay: `${(i + 2) * 0.15}s`, background: '#111111', border: '1px solid rgba(255,255,255,0.05)', padding: '48px 36px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 24, right: 24, fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', color: 'rgba(232,160,32,0.3)', letterSpacing: '0.1em' }}>{num}</div>
              <div style={{ marginBottom: 24 }}>{icon}</div>
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.4rem', fontWeight: 600, color: '#E8E8E8', marginBottom: 16 }}>{title}</h3>
              <p style={{ color: '#666', fontSize: '0.88rem', lineHeight: 1.8, marginBottom: 28, fontWeight: 300 }}>{desc}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {tags.map(t => (
                  <span key={t} style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', letterSpacing: '0.1em', color: '#E8A020', border: '1px solid rgba(232,160,32,0.2)', padding: '4px 10px', textTransform: 'uppercase' }}>{t}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Stats Band ─────────────────────────────────────────────────────────
function Stats() {
  const [ref, inView] = useInView(0.3);
  const c1 = useCounter(500, 1600, inView);
  const c2 = useCounter(200, 1800, inView);
  const c3 = useCounter(10000, 2000, inView);
  const c4 = useCounter(98, 1400, inView);
  const stats = [
    { value: c1, suffix: '+', label: 'Active Job Listings' },
    { value: c2, suffix: '+', label: 'Partner Companies' },
    { value: c3, suffix: '+', label: 'Registered Candidates' },
    { value: c4, suffix: '%', label: 'Placement Satisfaction' },
  ];
  return (
    <div ref={ref} style={{ background: '#E8A020', padding: '64px 5%' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 40, textAlign: 'center' }}>
        {stats.map(({ value, suffix, label }, i) => (
          <div key={label} className={inView ? 'animate-fade-up' : ''} style={{ animationDelay: `${i * 0.12}s` }}>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '3rem', fontWeight: 700, color: '#0D0D0D', lineHeight: 1 }}>
              {value.toLocaleString('en-IN')}{suffix}
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.68rem', letterSpacing: '0.14em', color: 'rgba(13,13,13,0.6)', textTransform: 'uppercase', marginTop: 8 }}>{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Careers Section ────────────────────────────────────────────────────
function Careers() {
  const [ref, inView] = useInView();
  const roles = [
    { title: 'Talent Acquisition Lead', dept: 'HR & Recruitment', type: 'Full-Time', location: 'Pune' },
    { title: 'Automotive Domain Consultant', dept: 'Business Development', type: 'Full-Time', location: 'Mumbai' },
    { title: 'Platform Operations Executive', dept: 'Operations', type: 'Full-Time', location: 'Pune' },
    { title: 'Client Relationship Manager', dept: 'Account Management', type: 'Full-Time', location: 'Bengaluru' },
  ];
  return (
    <section id="careers" ref={ref} style={{ padding: 'clamp(80px, 10vw, 120px) 5%', background: '#111111' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 80, alignItems: 'start' }}>
          <div>
            <div className={`section-label ${inView ? 'animate-fade-up' : ''}`} style={{ marginBottom: 20 }}>◈ &nbsp;Join Our Team</div>
            <div className={`divider-amber ${inView ? 'animate-fade-up delay-1' : ''}`} style={{ marginBottom: 32 }}/>
            <h2 className={`${inView ? 'animate-fade-up delay-2' : ''}`}
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', fontWeight: 700, color: '#E8E8E8', marginBottom: 24, lineHeight: 1.2 }}>
              Grow With the<br/>Industry You Serve
            </h2>
            <p className={`${inView ? 'animate-fade-up delay-3' : ''}`}
              style={{ color: '#666', lineHeight: 1.9, fontSize: '0.92rem', marginBottom: 32, fontWeight: 300 }}>
              We are a small, driven team working at the intersection of HR and automotive. If you understand the industry and want to help shape careers within it — we want to meet you.
            </p>
            <div className={inView ? 'animate-fade-up delay-4' : ''} style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 36 }}>
              {['Transparent hiring — no surprise rounds', 'Competitive pay benchmarked to industry', 'Remote-friendly with Pune as home base', 'Direct exposure to OEM leadership network'].map(p => (
                <div key={p} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{ color: '#E8A020', marginTop: 2, flexShrink: 0 }}>▸</span>
                  <span style={{ color: '#777', fontSize: '0.88rem', lineHeight: 1.7 }}>{p}</span>
                </div>
              ))}
            </div>
            <a href="#contact" className={`btn-primary ${inView ? 'animate-fade-up delay-5' : ''}`}>Send Your Resume</a>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {roles.map(({ title, dept, type, location }, i) => (
              <div key={title} className={`card-hover ${inView ? 'animate-fade-up' : ''}`}
                style={{ animationDelay: `${(i + 2) * 0.12}s`, background: '#141414', border: '1px solid rgba(255,255,255,0.05)', padding: '24px 28px', cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#E8E8E8', marginBottom: 6, fontSize: '0.92rem' }}>{title}</div>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.68rem', color: '#666', letterSpacing: '0.1em' }}>{dept}</div>
                  </div>
                  <span style={{ color: '#E8A020', fontSize: '1rem', flexShrink: 0 }}>→</span>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', color: '#555', border: '1px solid rgba(255,255,255,0.06)', padding: '3px 8px' }}>{type}</span>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', color: '#555', border: '1px solid rgba(255,255,255,0.06)', padding: '3px 8px' }}>📍 {location}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Contact Section ────────────────────────────────────────────────────
function Contact() {
  const [ref, inView] = useInView();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 4000);
    setForm({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <section id="contact" ref={ref} style={{ padding: 'clamp(80px, 10vw, 120px) 5%', background: '#0D0D0D', position: 'relative' }} className="grid-bg">
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 100%, rgba(232,160,32,0.05) 0%, transparent 60%)', pointerEvents: 'none' }}/>
      <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: 72 }}>
          <div className={`section-label ${inView ? 'animate-fade-up' : ''}`} style={{ marginBottom: 20 }}>◈ &nbsp;Get In Touch</div>
          <h2 className={`${inView ? 'animate-fade-up delay-2' : ''}`}
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, color: '#E8E8E8' }}>
            Start a Conversation
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 64, alignItems: 'start' }}>
          {/* Info */}
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
              {[
                {
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 2C7.24 2 5 4.24 5 7c0 4 5 11 5 11s5-7 5-11c0-2.76-2.24-5-5-5zm0 6.5A1.5 1.5 0 118.5 7 1.5 1.5 0 0110 8.5z" fill="#E8A020"/></svg>
                  ),
                  label: 'Location', value: 'Pune, Maharashtra, India'
                },
                {
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="5" width="16" height="12" rx="1" stroke="#E8A020" strokeWidth="1.5"/><polyline points="2,5 10,12 18,5" stroke="#E8A020" strokeWidth="1.5" strokeLinejoin="round"/></svg>
                  ),
                  label: 'Email', value: 'careers@automotivetalenthub.online'},
                {
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 3h3l1.5 4-2 1.5a10 10 0 004 4L12 10.5l4 1.5v3a1 1 0 01-1 1C6.716 16 3 9 3 4a1 1 0 011-1z" stroke="#E8A020" strokeWidth="1.5" fill="none"/></svg>
                  ),
                  label: 'Phone', value: '+91 98765 43210'
                },
              ].map(({ icon, label, value }, i) => (
                <div key={label} className={inView ? 'animate-fade-up' : ''} style={{ animationDelay: `${(i + 2) * 0.12}s`, display: 'flex', gap: 18, alignItems: 'flex-start' }}>
                  <div style={{ width: 44, height: 44, border: '1px solid rgba(232,160,32,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {icon}
                  </div>
                  <div>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', letterSpacing: '0.16em', color: '#555', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
                    <div style={{ color: '#B8B8B8', fontSize: '0.9rem' }}>{value}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className={inView ? 'animate-fade-up delay-6' : ''}
              style={{ marginTop: 48, padding: '28px', background: '#111111', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', letterSpacing: '0.16em', color: '#E8A020', marginBottom: 12, textTransform: 'uppercase' }}>Operating Hours</div>
              <div style={{ color: '#777', fontSize: '0.85rem', lineHeight: 1.8 }}>Monday – Saturday<br/>9:00 AM – 7:00 PM IST</div>
            </div>
          </div>
          {/* Form */}
          <div className={inView ? 'animate-fade-up delay-3' : ''}>
            {sent ? (
              <div style={{ background: 'rgba(232,160,32,0.08)', border: '1px solid rgba(232,160,32,0.3)', padding: '32px', textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', marginBottom: 12 }}>✓</div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.2rem', color: '#E8E8E8', marginBottom: 8 }}>Message Received</div>
                <div style={{ color: '#666', fontSize: '0.85rem' }}>We'll be in touch within 24 hours.</div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <input className="form-input" placeholder="Your Name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}/>
                  <input className="form-input" placeholder="Email Address" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}/>
                </div>
                <input className="form-input" placeholder="Subject" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}/>
                <textarea className="form-input" rows={5} placeholder="Your message..." required value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} style={{ resize: 'vertical', fontFamily: "'DM Sans', sans-serif" }}/>
                <button type="submit" className="btn-primary" style={{ alignSelf: 'flex-start' }}>Send Message</button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Footer ─────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer style={{ background: '#080808', borderTop: '1px solid rgba(255,255,255,0.04)', padding: '48px 5% 32px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 24, marginBottom: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Logo size={32} />
            <div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '0.9rem', fontWeight: 700, color: '#E8E8E8' }}>AutomotiveTalentHub</div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', letterSpacing: '0.14em', color: '#555', textTransform: 'uppercase' }}>Pune, India</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
            {['About', 'Services', 'Careers', 'Contact'].map(l => (
              <a key={l} href={`#${l.toLowerCase()}`} className="nav-link" style={{ fontSize: '0.7rem' }}>{l}</a>
            ))}
          </div>
        </div>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', color: '#444', letterSpacing: '0.08em' }}>
            © {new Date().getFullYear()} AutomotiveTalentHub. All rights reserved.
          </div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', color: '#444', letterSpacing: '0.08em' }}>
            Designed & Developed by{' '}
            <span style={{ color: '#E8A020' }}>Matisar Technologies</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ── App ────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <>
      <Navbar />
      <Hero />
      {/* <Ticker /> */}
      <About />
      <Services />
      <Stats />
      <Careers />
      <Contact />
      <Footer />
    </>
  );
}
